import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, Alert, Badge } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Components
import PolyShiftGroupsManager from "./components/PolyShiftGroupsManager";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import GeneralSettings from "../../Components/Common/GeneralSettings";
import PlotContainer from "../../Components/Common/PlotContainer";

// Config
import {
  polyShiftConfig,
  polyShiftDefaults,
} from "../../config/plotConfigs/polyShiftConfig";

// API
import {
  getPolyShiftData,
  generatePolyShiftPlot,
  fetchJobStatus,
} from "../../helpers/eirna_backend_helper";

// Custom hooks
import { usePolyShiftSettings } from "./hooks/usePolyShiftSettings";
import { useFileSelection } from "./hooks/useFileSelection";
import { usePolyShiftGroups } from "./hooks/usePolyShiftGroups";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  usePolyShiftShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";
import ActionButton from "../../Components/Common/ActionButton";

const PolyShift = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [loading, setLoading] = useState(true);
  const [plotLoading, setPlotLoading] = useState(false);
  const [plotBundle, setPlotBundle] = useState({});
  const [activePlot, setActivePlot] = useState('shift');
  const [error, setError] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    default_tran: "",
    seq_types: [],
    studies_dict: {},
    subgroup_present: false,
  });

  // Add ref for polling cleanup
  const pollIntervalRef = useRef(null);

  // Custom hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = usePolyShiftSettings();

  const {
    selectedSeqType,
    selectedGroup,
    setSelectedSeqType,
    setSelectedGroup,
  } = useFileSelection();

  const {
    timepoints,
    addTimepoint,
    removeTimepoint,
    updateTimepointName,
    addFilesToTimepoint,
    removeFilesFromTimepoint,
    clearTimepoint,
    clearAllTimepoints,
    validateTimepoints,
    getMasterFileDict,
    currentMaxVisibleTimepoint,
    getTimepointStats,
    loadTimepointData, // For loading shared link data
  } = usePolyShiftGroups(apiData.json_files || []);

  // Use the PolyShift specific short code handler
  const {autoRunPlot, 
         setAutoRunPlot,
         consumeUserPassedShortCode } =
    usePolyShiftShortCode({
      location,
      updateMultipleSettings,
      loadTimepointData,
    });

  // Cleanup effect for polling
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime plotBundle changes
  useScrollToRef(plotContainerRef, plotBundle);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getPolyShiftData(projectId);
        setApiData(response);

        if (response.default_tran) {
          updateSetting("customTranscriptList", response.default_tran);
        }

        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];
        if (fileTypes.length > 0) {
          setSelectedSeqType(fileTypes[0]);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        toast.success("Project data loaded successfully");
      } catch (err) {
        console.error("Fetch data error:", err);
        setError("Failed to load project data");
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, updateSetting, setSelectedSeqType, setSelectedGroup]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        timepointsWithFiles: timepoints.filter(
          (tp) => tp.controlFiles.length > 0 || tp.treatFiles.length > 0
        ).length,
      });

      const hasFiles = timepoints.some(
        (tp) => tp.controlFiles.length > 0 || tp.treatFiles.length > 0
      );

      if (!plotLoading && !loading && hasFiles) {
        console.log(
          "All conditions met, generating Polysome-Seq analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGeneratePlot();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    timepoints
      .map((tp) => tp.controlFiles.length + tp.treatFiles.length)
      .join(","),
    setAutoRunPlot,
  ]);

  // Filter files based on selected seq type and group
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  // Get unique seq types and groups
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  // Build payload with proper data types and short code support
  const buildPayload = useCallback(() => {
    const shortCodeForThisRequest = consumeUserPassedShortCode();
    // Use the PolyShift specific payload creator
    return createPlotSpecificPayload.polyShift({
      settings,
      projectId,
      userPassedShortCode: shortCodeForThisRequest,
      getMasterFileDict,
    });
  }, [projectId, settings, consumeUserPassedShortCode, getMasterFileDict]);

  // Enhanced polling function with proper cleanup
  const pollTaskStatus = useCallback(
    async (id) => {
      try {
        const response = await fetchJobStatus(id);
        const data = response?.data || response || {};

        if (["SUCCESS", "FAILURE"].includes(data.state)) {
          // Clear polling interval on completion
          if (pollIntervalRef.current) {
            clearTimeout(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }

          setPlotLoading(false);
          setTaskId(null);

          if (data.state === "SUCCESS") {
            if (data.result?.message === "user logged out") {
              navigate("/login");
              return;
            }
            if (data.result?.message) {
              setError(data.result.message);
              toast.error(data.result.message);
            } else if (data.result) {
              // Handle plot results - PolyShift has shift_heatmap and heatmap_comparison
              const plotResult = {
                shift_heatmap: data.result.shift_heatmap || null,
                heatmap_comparison: data.result.heatmap_comparison || null,
              };
              setPlotBundle(plotResult);
              setError(null);
              toast.success("PolyShift plot generated successfully");
            }
          } else {
            const m = `Job failed. Task ID: ${id}`;
            setError(m);
            toast.error(m);
          }
        } else {
          // Use ref to store timeout ID for cleanup
          pollIntervalRef.current = setTimeout(() => pollTaskStatus(id), 2000);
        }
      } catch (err) {
        console.error("Polling error:", err);

        // Clear polling on error
        if (pollIntervalRef.current) {
          clearTimeout(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        const m = `Unable to fetch job status. Task ID: ${id}`;
        setError(m);
        toast.error(m);
        setPlotLoading(false);
        setTaskId(null);
      }
    },
    [navigate]
  );

  // Handle generate plot
  const handleGeneratePlot = useCallback(async () => {
    if (plotLoading) {
      console.log("Job already in progress");
      return;
    }

    // Validate settings
    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Validate timepoints
    const timepointValidation = validateTimepoints();
    if (!timepointValidation.isValid) {
      toast.error(timepointValidation.errors[0]);
      return;
    }

    // Check Z-score recommendations based on original JS logic
    const totalTimepoints = timepoints.filter(
      (tp) => tp.controlFiles.length > 0 || tp.treatFiles.length > 0
    ).length;

    // Skip confirmation dialogs when auto-running from shared link
    if (!autoRunPlot) {
      if (settings.zscoreCheck && totalTimepoints === 2) {
        const confirmed = window.confirm(
          "It is recommended to turn off the 'Use Z-score' checkbox when only using 2 fractions. Press cancel to go back and change it, press OK to continue generating the plot."
        );
        if (!confirmed) return;
      }

      if (!settings.zscoreCheck && totalTimepoints >= 3) {
        const confirmed = window.confirm(
          "It is recommended to turn on the 'Use Z-score' checkbox when using 3 or more fractions. Press cancel to go back and change it, press OK to continue generating the plot."
        );
        if (!confirmed) return;
      }
    }

    try {
      setPlotLoading(true);
      setError(null);

      const payloadData = buildPayload();
      console.log("PolyShift payload:", payloadData);

      const response = await generatePolyShiftPlot(payloadData);
      const data = response?.data || response || {};

      if (data.task_id) {
        setTaskId(data.task_id);
        toast.info(`Job started (ID: ${data.task_id})`);
        pollTaskStatus(data.task_id);
      } else if (data.message) {
        if (data.message === "user logged out") {
          navigate("/login");
          return;
        }
        setError(data.message);
        toast.error(data.message);
        setPlotLoading(false);
      } else if (data.shift_heatmap || data.heatmap_comparison) {
        setPlotBundle({
          shift: data.shift_heatmap ?? undefined,
          comparison: data.heatmap_comparison ?? undefined,
        });
        setActivePlot(response.shift_heatmap ? 'shift' : 'comparison');
        setError(null);
        toast.success("PolyShift plot generated successfully");
        setPlotLoading(false);
      } else {
        setError("No valid response received");
        toast.error("No valid response received");
        setPlotLoading(false);
      }
    } catch (err) {
      console.error("PolyShift plot error:", err);
      let errorMsg = "Failed to generate PolyShift plot";
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      toast.error(errorMsg);
      setPlotLoading(false);
    }
  }, [
    plotLoading,
    validateSettings,
    validateTimepoints,
    buildPayload,
    pollTaskStatus,
    navigate,
    settings.zscoreCheck,
    timepoints,
    autoRunPlot, // Include for confirmation skip logic
  ]);

  if (loading) {
    return (
      <div className="page-content">
        <Container
          fluid
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <div className="text-center">
            <FadeLoader color="#1A6D51" />
          </div>
        </Container>
      </div>
    );
  }

  if (error && !Array.isArray(apiData.json_files)) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger" className="text-center">
            <h4>Error Loading Project</h4>
            <p>{error}</p>
            <Button color="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  // Use the stats from the hook
  const stats = getTimepointStats;
  const totalFilesAssigned = stats.totalFiles;

  document.title = "Polysome-Seq Analysis | EIRNA Bio Connect";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          breadcrumbs={[
            { label: "Home", url: "/dashboard" },
            {
              label: "Select Function",
              url: `/projects/${projectId}/select-function`,
            },
            { label: "Polysome-Seq Analysis" },
          ]}
        />

        {/* Header with stats */}
        <Row className="mb-3">
          <Col lg={12}>
            <div className="d-flex justify-content-between align-items-center">
              <h4
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#1f2937",
                  marginBottom: "0",
                }}
              >
                Polysome-Seq Analysis
              </h4>
            </div>
          </Col>
        </Row>

        {/* First Row: General Settings and Seq Types/Groups */}
        <Row className="mb-4">
          <Col lg={12}>
            <GeneralSettings
              settings={settings}
              onUpdateSetting={updateSetting}
              onReset={resetSettings}
              config={polyShiftConfig}
              title="PolyShift Settings"
            />
          </Col>{" "}
        </Row>
        <Row className="mb-4">
          <Col lg={12}>
            <SeqTypesAndGroups
              seqTypes={seqTypes}
              groups={availableGroups}
              selectedSeqType={selectedSeqType}
              selectedGroup={selectedGroup}
              onSelectSeqType={setSelectedSeqType}
              onSelectGroup={setSelectedGroup}
              showSelectAllCheckboxes={false}
              customTitle="Seq Types & Groups"
              customIcon="ri-tools-fill"
            />
          </Col>
        </Row>

        {/* Second Row: PolyShift Groups Manager */}
        <Row className="mb-4">
          <Col lg={12}>
            <PolyShiftGroupsManager
              files={filteredFiles}
              allFiles={apiData.json_files}
              timepoints={timepoints}
              settings={settings}
              onUpdateSetting={updateSetting}
              onAddTimepoint={addTimepoint}
              onRemoveTimepoint={removeTimepoint}
              onUpdateTimepointName={updateTimepointName}
              onAddFilesToTimepoint={addFilesToTimepoint}
              onRemoveFilesFromTimepoint={removeFilesFromTimepoint}
              onClearTimepoint={clearTimepoint}
              onClearAll={clearAllTimepoints}
              currentMaxVisible={currentMaxVisibleTimepoint}
            />
          </Col>
        </Row>

        {/* Generate Button and Status */}
        <Row className="mb-1">
          <Col lg={12}>
            <div ref={loadingDivRef} 
                 className="d-flex justify-content-between align-items-center mb-3">
              <div className="text-muted">
                <i className="ri-folder-line me-1"></i>
                Files Assigned: <strong>{totalFilesAssigned}</strong>
                {totalFilesAssigned === 0 ? (
                  <span className="text-warning ms-2">
                    Please assign files to fractions first
                  </span>
                ) : (
                  <i className="ri-check-circle-line ms-2 text-success"></i>
                )}
                {taskId && (
                  <span className="ms-2">
                    - <strong>Task ID:</strong> {taskId}
                  </span>
                )}
              </div>
              <ActionButton
                onClick={handleGeneratePlot}
                loading={plotLoading}
                buttonType="plot"
              />{" "}
            </div>
          </Col>
        </Row>

        {/* Results Container */}
        {(plotBundle || plotLoading || error) && (
          <React.Fragment>
            {plotBundle && (
              <Row className="mb-3">
                <Col lg={12} className="d-flex gap-2">
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${activePlot === 'shift' ? 'active' : ''}`}
                    onClick={() => setActivePlot('shift')}
                    disabled={!plotBundle.shift_heatmap}
                  >
                    Shift heatmap
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${activePlot === 'comparison' ? 'active' : ''}`}
                    onClick={() => setActivePlot('comparison')}
                    disabled={!plotBundle.heatmap_comparison}
                  >
                    Heatmap comparison
                  </button>
                </Col>
              </Row>
            )}

            <Row>
              <Col lg={12}>
                <div style={{ display: activePlot === 'shift' ? 'block' : 'none' }}>
                  <PlotContainer
                    ref={plotContainerRef}
                    key="shift-heatmap"
                    plotData={plotBundle.shift_heatmap || null}
                    loading={plotLoading}
                    error={error}
                    selectedFilesCount={totalFilesAssigned}
                    title="Shift Heatmap"
                    onRetry={handleGeneratePlot}
                    showFileCount={true}
                  />
                </div>

                <div style={{ display: activePlot === 'comparison' ? 'block' : 'none' }}>
                  <PlotContainer
                    key="heatmap-comparison"
                    plotData={plotBundle.heatmap_comparison || null}
                    loading={plotLoading}
                    error={error}
                    selectedFilesCount={totalFilesAssigned}
                    title="Heatmap Comparison"
                    onRetry={handleGeneratePlot}
                    showFileCount={true}
                  />
                </div>
              </Col>
            </Row>
          </React.Fragment>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </Container>
    </div>
  );
};

export default PolyShift;
